import { render, h } from 'preact';
import bem from 'easy-bem';
import {
  CheckoutProvider,
  StoreProvider,
} from '@primer-io/shared-library/contexts';

import { locateElement } from '../../utils/dom';
import { PaymentMethods } from '../../payment-methods/PaymentMethods';
import { CheckoutPaymentMethodConfig } from '../CheckoutPaymentMethodConfig';
import { CheckoutTokenizationHandlers } from '../CheckoutTokenizationHandlers';
import { BasePaymentMethod } from '../../payment-methods/BasePaymentMethod';
import {
  CheckoutOptions,
  InputValidationError,
  PrimerCheckout,
  SupportedLocale,
} from '../../types';
import { Vault } from '../Vault';
import { getAttribute, setValue } from '../DomUtilities';
import { ElementID, Scene } from '../../enums/Checkout';
import { Translations } from '../../assets/translations/Translations';
import { IFrameEventType } from '../../core/IFrameEventType';
import { IFrameMessagePayload } from '../../core/IFrameMessage';
import { ClientContext } from '../../core/ClientContextFactory';

import { createCheckoutViewUtils } from '../ui/ViewUtils';

import CheckoutRoot from '../../scenes/CheckoutRoot';

import { SceneTransition } from '../SceneTransition';
import { Environment } from '../../utils/Environment';
import { createStore } from '../../store/CheckoutStoreFactory';
import { isCardholderNameRequired } from '../../payment-methods/credit-card/utils';
import { getSystemLocale } from '../../utils/getSystemLocale';
import {
  createSetBusinessDetails,
  createSetCustomerDetails,
  createSetOrderDetails,
  createSetPurchaseInfo,
} from '../OptionUpdater';
import { CheckoutModules } from '../../checkout-modules/CheckoutModules';
import { teardownUI } from '../Teardown';

const renderRoot = ({
  options,
  checkout,
  viewUtils,
  context,
  transitions,
  store,
  root,
}: {
  options;
  checkout;
  viewUtils;
  context?;
  transitions;
  store;
  root;
}) =>
  render(
    <CheckoutProvider
      value={{
        options,
        checkout,
        viewUtils,
        context,
        transitions,
        className: bem('PrimerCheckout'),
      }}
    >
      <StoreProvider value={store}>
        <CheckoutRoot />
      </StoreProvider>
    </CheckoutProvider>,
    root,
  );

export class Checkout {
  static async create(
    getContext: () => Promise<ClientContext>,
    options: CheckoutOptions,
  ): Promise<PrimerCheckout> {
    const parent = locateElement(options.container);

    if (parent == null) {
      return onConfigError(
        `Attempted to mount checkout UI at "${options.container}" but the element could not be found`,
      );
    }

    const root = document.createElement('div');
    parent.prepend(root);

    ///////////////////////////////////////////
    // Context
    ///////////////////////////////////////////
    const contextPromise = getContext();

    ///////////////////////////////////////////
    // Render
    ///////////////////////////////////////////
    const store = createStore();
    store.setLocale(options.locale ?? (getSystemLocale() as SupportedLocale));

    const viewUtils = createCheckoutViewUtils(store, options);
    const transitions = new SceneTransition();

    async function validateCardForm() {
      let valid = false;
      const validationErrors: InputValidationError[] = [];

      const token = store.getSelectedVaultToken();

      if (token) {
        valid = true;
      } else {
        const cardValidation = await paymentMethods.card.validate();
        let uxValid = true;

        if (cardholderNameRequired) {
          const value = getAttribute(
            ElementID.CARDHOLDER_NAME_INPUT,
            'value',
          )?.trim();
          const error = value ? null : translation.cardNameRequired;
          viewUtils.fieldUtils.setErrorState(ElementID.CARDHOLDER_NAME, error);
          if (error) {
            validationErrors.push({
              name: 'cardholderName',
              error: 'cardNameRequired',
              message: error,
            });
          }
          uxValid = !!value;
        }

        valid = cardValidation.valid && uxValid;
        validationErrors.push(...cardValidation.validationErrors);
      }

      return {
        valid,
        validationErrors,
      };
    }

    async function tokenizeCard() {
      const token = store.getSelectedVaultToken();

      const { valid } = await validateCardForm();

      if (!valid) {
        return;
      }

      if (token) {
        store.setIsLoading(true);
        config.onTokenizeSuccess(token);
        return;
      }

      await paymentMethods.card.tokenize();
    }

    const checkout = {
      validate: validateCardForm,
      tokenize: tokenizeCard,
    };

    renderRoot({ options, checkout, viewUtils, transitions, store, root });

    ///////////////////////////////////////////
    // State
    ///////////////////////////////////////////
    const translation = Translations.get(options.locale);
    const cardOptions = options.card || {};
    const cardholderNameRequired = isCardholderNameRequired(cardOptions);

    store.produceState((draft) => {
      draft.translation = translation;
      draft.options.showSavePaymentMethod = false;
      draft.form.inputLabelsVisible = options.form?.inputLabelsVisible ?? true;
    });

    store.setCustomerDetails(options.customerDetails ?? null, 'CLIENT');
    store.setBusinessDetails(options.businessDetails ?? null, 'CLIENT');
    if (options.purchaseInfo && !options.orderDetails) {
      console.warn(
        'purchaseInfo has been deprecated, use orderDetails instead',
      );
      store.setPurchaseInfo(options.purchaseInfo ?? null);
    } else {
      if (options.purchaseInfo && options.orderDetails) {
        console.warn(
          'both purchaseInfo and orderDetails have been provided, orderDetails will take preference',
        );
      }
      store.setOrderDetails(options.orderDetails ?? null, 'CLIENT');
    }

    ///////////////////////////////////////////
    // Vault & Payment Methods
    ///////////////////////////////////////////
    const context = await contextPromise;

    const processCheckoutModules = async () => {
      const checkoutModules = context.session.checkoutModules
        .map((checkoutModule) =>
          CheckoutModules.create(checkoutModule, context, store, options),
        )
        .filter(Boolean);

      store.setCheckoutModules(checkoutModules);
    };

    const processVault = async () => {
      const vault = new Vault(context);
      store.setVault(vault);

      // Vault is visible by default
      const vaultVisible = options?.vault?.visible ?? true;

      // Hiding the vault = not fetching the vault data
      if (vaultVisible === undefined || vaultVisible === true) {
        const vaulted = await vault.fetch(options.customerId);

        vaulted.forEach((pm) => {
          store.addVault(pm);
        });

        store.selectFirstVault();
      }
    };

    const config = {
      ...CheckoutPaymentMethodConfig.create(store, viewUtils, options),
      ...CheckoutTokenizationHandlers.create(context, viewUtils, options),
    };
    let paymentMethods: Record<string, BasePaymentMethod> = {};
    const processPaymentMethods = async () => {
      paymentMethods = await PaymentMethods.create(
        context,
        config,
        viewUtils.store,
        viewUtils.styleManager,
      );

      store.setPaymentMethods(paymentMethods);
    };

    await Promise.all([
      processVault(),
      processPaymentMethods(),
      processCheckoutModules(),
    ]);

    context.messageBus.on(
      IFrameEventType.CARDHOLDER_NAME,
      (e: IFrameMessagePayload<string>) => {
        setValue(ElementID.CARDHOLDER_NAME_INPUT, e.payload);
      },
    );

    store.setScene(Scene.CHOOSE_PAYMENT_METHOD);
    store.setIsLoading(false);

    if (Environment.get('PRIMER_BUILD_INTEGRATION_BUILDER', false)) {
      // eslint-disable-next-line @typescript-eslint/no-var-requires, global-require
      const { CardProxy } = require('../CardProxy');

      const cardProxy = new CardProxy(context, config, paymentMethods.card);
      // @ts-expect-error for integration builder
      checkout.card = cardProxy;
    }

    renderRoot({
      options,
      checkout,
      viewUtils,
      transitions,
      store,
      root,
      context,
    });

    function teardown() {
      if (context.session.isTeardown) {
        throw new Error('Checkout has already been torn down');
      }

      context.session.isTeardown = true;

      teardownUI(root);
    }

    return {
      ...checkout,
      // remove once fully deprecated
      setPurchaseInfo: createSetPurchaseInfo(store),
      setCustomerDetails: createSetCustomerDetails(store, 'CLIENT'),
      setBusinessDetails: createSetBusinessDetails(store, 'CLIENT'),
      setOrderDetails: createSetOrderDetails(store, 'CLIENT'),
      teardown,
    };
  }
}

function onConfigError(message: string): PrimerCheckout {
  console.error(message);
  return {
    teardown: () => undefined,
    tokenize: () => Promise.resolve(),
    validate: () => Promise.resolve({ valid: false, validationErrors: [] }),
    // remove once fully deprecated
    setPurchaseInfo: () => Promise.resolve(),
    setCustomerDetails: () => Promise.resolve(),
    setBusinessDetails: () => Promise.resolve(),
    setOrderDetails: () => Promise.resolve(),
  };
}
